import { useCallback, useEffect, useMemo } from 'react';
import { useKnowledgeBaseListStore } from '../store/useKnowledgeBaseListStore';
import { KnowledgeBaseCategory } from '@edwin-group/people-graphql';
import type { Tab } from '@/components/ui/tabs';

const collectAllDescendantIds = (category: KnowledgeBaseCategory): string[] => {
  const ids: string[] = [category.documentId];
  if (category.subCategories?.length) {
    for (const sub of category.subCategories) {
      if (sub) {
        ids.push(...collectAllDescendantIds(sub as KnowledgeBaseCategory));
      }
    }
  }
  return ids;
};

const rootHasValidCategory = (root: KnowledgeBaseCategory, validCategoryIdSet: Set<string>): boolean => {
  const ids = collectAllDescendantIds(root);
  return ids.some((id) => validCategoryIdSet.has(id));
};

export const useKnowledgeBaseFilters = () => {
  const rootCategories = useKnowledgeBaseListStore((state) => state.rootCategories);
  const validCategoriesByType = useKnowledgeBaseListStore((state) => state.validCategoriesByType);
  const selectedCategoryIds = useKnowledgeBaseListStore((state) => state.selectedCategoryIds);
  const selectedSubcategoryIds = useKnowledgeBaseListStore((state) => state.selectedSubcategoryIds);
  const toggleCategory = useKnowledgeBaseListStore((state) => state.toggleCategory);
  const toggleSubcategory = useKnowledgeBaseListStore((state) => state.toggleSubcategory);
  const resetFilters = useKnowledgeBaseListStore((state) => state.resetFilters);

  const validCategoryIdSet = useMemo(() => {
    const ids = new Set<string>();
    Object.values(validCategoriesByType).forEach((categoryIds) => {
      categoryIds.forEach((id) => ids.add(id));
    });
    return ids;
  }, [validCategoriesByType]);

  const filteredRootCategories = useMemo(() => {
    if (validCategoryIdSet.size === 0) return [];
    const filtered = rootCategories.filter((root) => rootHasValidCategory(root, validCategoryIdSet));

    const articlesIndex = filtered.findIndex((root) => root.category === 'Articles');
    if (articlesIndex <= 0) {
      return filtered;
    }

    const [articlesCategory] = filtered.splice(articlesIndex, 1);
    return [articlesCategory, ...filtered];
  }, [rootCategories, validCategoryIdSet]);

  useEffect(() => {
    if (filteredRootCategories.length === 0) return;
    if (selectedCategoryIds.length > 0) return;

    const firstCategoryId = filteredRootCategories[0].documentId;
    toggleCategory(firstCategoryId);
  }, [filteredRootCategories, selectedCategoryIds.length, toggleCategory]);

  const tabs = useMemo<Tab[]>(
    () =>
      filteredRootCategories.map((rootCategory) => ({
        name: rootCategory.category,
        current: selectedCategoryIds.includes(rootCategory.documentId),
        onClick: () => toggleCategory(rootCategory.documentId),
      })),
    [filteredRootCategories, selectedCategoryIds, toggleCategory],
  );

  const selectableRootCategoryIdSet = useMemo(
    () => new Set(filteredRootCategories.map((r) => r.documentId)),
    [filteredRootCategories],
  );

  useEffect(() => {
    if (validCategoryIdSet.size === 0 && selectableRootCategoryIdSet.size === 0) return;
    const categoryInvalid = selectedCategoryIds.some((id) => !selectableRootCategoryIdSet.has(id));
    const subcategoryInvalid = selectedSubcategoryIds.some((id) => !validCategoryIdSet.has(id));
    if (categoryInvalid || subcategoryInvalid) {
      resetFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validCategoryIdSet.size, selectableRootCategoryIdSet, selectedCategoryIds, selectedSubcategoryIds, resetFilters]);

  const visibleSubcategories = useMemo(() => {
    if (selectedCategoryIds.length === 0) return [];
    const items: KnowledgeBaseCategory[] = [];
    for (const rootId of selectedCategoryIds) {
      const root = rootCategories.find((r) => r.documentId === rootId);
      if (root?.subCategories?.length) {
        for (const sub of root.subCategories) {
          if (sub && validCategoryIdSet.has((sub as KnowledgeBaseCategory).documentId)) {
            items.push(sub as KnowledgeBaseCategory);
          }
        }
      }
    }
    return items;
  }, [selectedCategoryIds, rootCategories, validCategoryIdSet]);

  const categorySelectValue = useMemo(() => {
    if (selectedCategoryIds.length === 0) return '';
    return selectedCategoryIds[0];
  }, [selectedCategoryIds]);

  const subcategoryOptions = useMemo(
    () =>
      visibleSubcategories.map((subcategory) => ({
        value: subcategory.documentId,
        label: subcategory.category,
      })),
    [visibleSubcategories],
  );

  const handleCategorySelect = useCallback(
    (value: string) => {
      if (!value) return;
      if (selectedCategoryIds[0] === value) return;
      if (selectedCategoryIds.length > 0) {
        toggleCategory(selectedCategoryIds[0]);
      }
      toggleCategory(value);
    },
    [selectedCategoryIds, toggleCategory],
  );

  const handleSubcategoryChange = useCallback(
    (newIds: string[]) => {
      const toRemove = selectedSubcategoryIds.filter((id) => !newIds.includes(id));
      const toAdd = newIds.filter((id) => !selectedSubcategoryIds.includes(id));
      toRemove.forEach((id) => toggleSubcategory(id));
      toAdd.forEach((id) => toggleSubcategory(id));
    },
    [selectedSubcategoryIds, toggleSubcategory],
  );

  const hasActiveFilters = selectedCategoryIds.length > 0 || selectedSubcategoryIds.length > 0;
  const hasSelectedCategories = selectedCategoryIds.length > 0;

  return {
    tabs,
    rootCategories: filteredRootCategories,
    selectedCategoryIds,
    selectedSubcategoryIds,
    toggleCategory,
    toggleSubcategory,
    visibleSubcategories,
    categorySelectValue,
    handleCategorySelect,
    subcategoryOptions,
    handleSubcategoryChange,
    hasActiveFilters,
    hasSelectedCategories,
    resetFilters,
  };
};
